import { Button, Flex, Form, Input, Switch } from 'antd';
import { useEffect } from 'react';
import {
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import useAuth from '@/hooks/useAuth';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';

type Props = CustomModalProps & {
  id?: string;
  data: any;
  type?: string;
};

const ManageDepartment = ({ id, data, type, open, handleClose }: Props) => {
  const [form] = Form.useForm();
  const { organizationId } = useAuth();

  // API hooks
  const [createDept, { isLoading: creating }] = useCreateDepartmentMutation();
  const [updateDept, { isLoading: updating }] = useUpdateDepartmentMutation();

  const handleSubmit = async (values: any) => {
    try {
      if (isEditMode && id) {
        await updateDept({
          data: {
            name: values.name,
            status: values?.status ? 'active' : 'inactive',
          },
          id,
        }).unwrap();
        toast('success', 'Department updated successfully');
      } else {
        await createDept({
          name: values.name,
          organization_id: organizationId,
          status: values?.status ? 'active' : 'inactive',
        }).unwrap();
        toast('success', 'Department created successfully');
      }

      handleClose();
      form.resetFields();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to save department');
      toast('error', errorMessage);
    }
  };

  const isEditMode = !!id;
  const isViewMode = type === 'VIEW';

  // Populate form when editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && data && open) {
      form.setFieldsValue({
        name: data?.name,
        status: data?.status === 'active' ? true : false,
      });
    } else if (!isEditMode && !isViewMode && open) {
      form.resetFields();
    }
  }, [data, form, isEditMode, isViewMode, open]);

  const getTitle = () => {
    if (isViewMode) return 'View Department';
    if (isEditMode) return 'Edit Department';
    return 'Add New Department';
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={getTitle()}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-6"
        disabled={isViewMode}
        onFinish={isViewMode ? undefined : handleSubmit}
      >
        <Form.Item
          label="Department Name"
          name="name"
          rules={[
            { required: !isViewMode, message: 'Please enter department name' },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Status"
          name="status"
          initialValue={true}
          layout="horizontal"
          className="border-b border-t !mb-0 !py-0.5 border-b-theme-gray-700 border-t-theme-gray-700 horizontal-end"
        >
          <Switch className="custom-switch" />
        </Form.Item>

        {!isViewMode && (
          <Flex justify="end">
            <Button
              className="btn-primary min-w-[80px] h-11 font-semibold"
              htmlType="submit"
              loading={creating || updating}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Flex>
        )}
      </Form>
    </CustomDrawer>
  );
};

export default ManageDepartment;
